import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { CATALOGUES, LOCALES, type Locale, type MessageKey } from './catalogue';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ `useT()`. One call per string, with a literal key.                        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **CALL `t()` WITH A LITERAL, ONE CALL PER BRANCH.** `t(keyFor(status))` is invisible to any
 * check that reads the source for literal arguments — every branch's entry then reports as an
 * orphan, and the fix somebody reaches for is *deleting the entry*. `MessageKey` makes a typo a
 * type error; only a literal makes the key findable.
 *
 * ⚠️ **Interpolation is `{name}`, and a missing value is LOUD.** A silent `{name}` left in the
 * output is a defect that reaches a screenshot: it reads as a template that failed, and nobody can
 * tell whether the value was empty or the key was wrong. So an unreplaced placeholder throws in
 * development and renders the key in production, which is ugly on purpose.
 */

interface I18n {
  readonly locale: Locale;
  readonly t: (key: MessageKey, values?: Readonly<Record<string, string | number>>) => string;
  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ SWITCH THE WHOLE APP'S LANGUAGE. Two callers, and both of them    │
   * │ exist because the stored preference used to do NOTHING.                │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * `preferences-boot.tsx` calls it once per sign-in with what the server holds, and the account
   * screen calls it when somebody picks a language — the same shape `applyTheme` has, for the same
   * reason (a setting whose effect waits for a reload reads as a setting that did not save).
   *
   * ⚠️ **`locale` above stays the TRUTH and this does not make it a hint.** The provider is still
   * controlled by its prop; this calls back to whoever owns that prop. So a test that pins
   * `locale="hu"` gets a no-op setter rather than a component that can wander out from under it.
   */
  readonly setLocale: (locale: Locale) => void;
}

const I18nContext = createContext<I18n | undefined>(undefined);

/** Turns `{a} and {b}` into a string, or complains. */
function format(
  template: string,
  key: string,
  values: Readonly<Record<string, string | number>> | undefined,
): string {
  if (!template.includes('{')) return template;

  return template.replace(/\{(\w+)\}/g, (_match, name: string) => {
    const value = values?.[name];
    if (value === undefined) {
      /* ⚠️ Not a silent fallback. A message with an unfilled placeholder is wrong in a way that
       * looks like a data problem, and the cheapest place to find out is here. */
      if (import.meta.env.DEV) {
        throw new Error(`the message ${key} needs a value for {${name}}`);
      }
      return `{${name}}`;
    }
    return String(value);
  });
}

export function I18nProvider({
  locale,
  onLocaleChange,
  children,
}: {
  readonly locale: Locale;
  /**
   * ⚠️ **Optional, and its absence is what keeps ninety-odd test call sites honest.** A suite that
   * renders one screen at a pinned locale wants `setLocale` to do nothing; the application passes
   * this so the account screen and the boot can move it.
   */
  readonly onLocaleChange?: (locale: Locale) => void;
  readonly children: ReactNode;
}) {
  const value = useMemo<I18n>(
    () => ({
      locale,
      t: (key, values) => format(CATALOGUES[locale][key], key, values),
      setLocale: (next) => {
        onLocaleChange?.(next);
      },
    }),
    [locale, onLocaleChange],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const value = useContext(I18nContext);
  /* ⚠️ Throws rather than defaulting to Hungarian. A component rendered outside the provider would
   * otherwise work perfectly in one locale and be untranslatable in the other, and the missing
   * provider would surface as a mysteriously monolingual screen. */
  if (value === undefined) {
    throw new Error('useI18n was called outside I18nProvider');
  }
  return value;
}

export function useT(): I18n['t'] {
  return useI18n().t;
}

/**
 * ⚠️ **`navigator.languages` is typed non-nullable and is not always present.** jsdom and older
 * engines leave it undefined, so the types say the `??` below is unnecessary and the runtime
 * disagrees — hence reading it through `unknown` rather than suppressing the lint rule, which
 * would also suppress a real one later.
 */
function browserLanguages(): readonly string[] {
  /* Read as `unknown` off a widened `globalThis`, because the DOM types promise `navigator` and
   * `navigator.languages` both exist and neither promise holds in every runtime this code runs in. */
  const languages = (globalThis as { navigator?: { languages?: unknown } }).navigator?.languages;
  return Array.isArray(languages) ? (languages as readonly string[]) : [];
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE LANGUAGE HINT, WHICH IS THE THEME'S ARGUMENT APPLIED TO WORDS   │
 * │ RATHER THAN TO COLOURS.                                                   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `user_preferences.locale` is the source of truth and it cannot be known before the first round
 * trip. The theme has the same problem and solves it by caching the resolved id locally so
 * `index.html` can paint before the first frame — *"the cache is never read as the answer, only as
 * the guess"*. Words need it more than colours do: a Hungarian member on an English laptop would
 * otherwise get the entire interface in English on every cold start and watch it snap to Hungarian
 * a moment later, which reads as a page that loaded wrong.
 *
 * ⚠️ **Wrapped, because storage THROWS rather than returning null in a locked-down browser** —
 * Safari's private mode and any profile with site data blocked raise on access. A language hint is
 * not worth taking the application down for.
 */
export const LOCALE_HINT_KEY = 'forecastify.locale';

function storedLocale(): Locale | undefined {
  try {
    const stored = globalThis.localStorage.getItem(LOCALE_HINT_KEY);
    return LOCALES.find((locale) => locale === stored);
  } catch {
    return undefined;
  }
}

/** Record the resolved choice for the next cold start. See `LOCALE_HINT_KEY`. */
export function rememberLocale(locale: Locale): void {
  try {
    globalThis.localStorage.setItem(LOCALE_HINT_KEY, locale);
  } catch {
    /* No cache, so the next cold start guesses from the browser again. That is the whole cost. */
  }
}

/**
 * The locale to start in, before the server has been asked.
 *
 * ⚠️⚠️ **THE HINT COMES FIRST, AND THAT ORDER IS THE FIX.** The account screen has had a language
 * dropdown since C1; it wrote `user_preferences.locale`, the API read it for mail and for the
 * Agent's replies, and **nothing between sign-in and the first frame ever read it** — so the
 * interface followed `navigator.languages` and the Agent followed the setting, and the two
 * disagreed for anybody whose browser was not Hungarian. Reported as *"the Agent does not respect
 * the language setting"*, which was the half that was working.
 *
 * ⚠️ **Hungarian is the DEFAULT, not the fallback**, per `decisions.md` R-6 — the product is
 * Hungarian first, and an unrecognised `navigator.language` lands on HU rather than on English.
 */
export function preferredLocale(languages: readonly string[] = browserLanguages()): Locale {
  const remembered = storedLocale();
  if (remembered !== undefined) return remembered;

  for (const language of languages) {
    const short = language.slice(0, 2).toLowerCase();
    const match = LOCALES.find((locale) => locale === short);
    if (match !== undefined) return match;
  }
  return 'hu';
}

/**
 * ⚠️ **Exported so a helper OUTSIDE a component can take `t` as a parameter.** A screen with a
 * per-branch label function (`viewLabel(t, view)`) needs to type that parameter, and the alternative
 * — `ReturnType<typeof useT>` at each call site — spells the same type four different ways.
 *
 * The per-branch shape exists because `i18n:check` reads **literal** keys only: `t(keyFor(view))` is
 * invisible to it, so every branch's entry reports as an orphan and the fix people reach for is
 * deleting the entry.
 */
export type Translate = I18n['t'];

export { LOCALES, type Locale, type MessageKey } from './catalogue';
